
import { Signal } from "signals";
import Components from "slotworks/components/components";
import ReelComponent from "slotworks/components/matrix/reel/reel-component";
import AbstractReelTransition from "slotworks/components/matrix/reel/transition-behaviours/abstract-reel-transition";
import ReelSpinner from "slotworks/components/matrix/reel/transition-behaviours/spin/reel-spinner";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import { AnticipationInfo } from "slotworks/model/gameplay/records/spin-record";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import { parallel } from "slotworks/utils/race-utils";

export default class SpinReelTransition extends AbstractReelTransition {

    public onReelLand: Signal = new Signal();

    private spinners: ReelSpinner[];

    private startGap: number = 100;
    private stopGap: number = 100;
    private quickStopGap: number = 50;

    private allowPartialSkips: boolean = true;

    private anticipation: AnticipationInfo;
    private anticipatingSymbols: SymbolComponent[];

    private spinningReels: number;
    private skipping: boolean;

    private heldReels: number[] = [];

    // Race handler function, see checkRaceComplete
    private completeResolve: Function;

    constructor(startGap: number, stopGap: number, quickStopGap: number, allowPartialSkips: boolean) {
        super();

        this.startGap = startGap;
        this.stopGap = stopGap;
        this.quickStopGap = quickStopGap;
        this.allowPartialSkips = allowPartialSkips;
    }

    public init(reels: ReelComponent[], stops: number[], reelset: string[][]) {
        super.init(reels, stops, reelset);

        this.spinners = [];

        for (let i = 0; i < reels.length; i++) {
            const reel = reels[i];
            const spinner = new ReelSpinner(reel, stops[i], reelset[i]);
            this.spinners.push(spinner);
            spinner.landSignal.add((index: number) => this.reelLandedHandler(index));
        }
    }

    public start(reelset?: string[][], jumpStart: boolean = false): Contract<void> {
        this.skipping = false;

        SoundManager.event(SoundEvent.reel_transition_start);

        Components.matrix.resetSymbols();

        const startMethods = [];

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) === -1) {
                const spinner = this.spinners[spinnerIndex];
                startMethods.push(() => spinner.start(jumpStart ? 0 : this.startGap * spinnerIndex, jumpStart));
            }
        }

        this.spinningReels = startMethods.length;

        return parallel(startMethods);
    }

    public stop(stops: number[], reelset: string[][], quickSpin: boolean, anticipation: AnticipationInfo): Contract<void> {
        this.anticipatingSymbols = [];
        this.anticipation = anticipation;
        const anticipations = anticipation.reelAnticipations;

        const stopMethods: Array<() => Contract<void>> = [];

        let anticipating = false;

        if (anticipations) {
            for (const anticipate of anticipations) {
                if (anticipate) {
                    anticipating = true;
                }
            }
        }

        /* if (quickSpin) {
            anticipating = true;
        }*/

        for (let spinnerIndex = 0; spinnerIndex < this.spinners.length; spinnerIndex++) {
            if (this.heldReels.indexOf(spinnerIndex) !== -1) {
                continue;
            }

            const spinner = this.spinners[spinnerIndex];

            if (anticipations && anticipations[spinnerIndex]) {
                stopMethods.push(() => spinner.anticipate(stops[spinnerIndex], reelset[spinnerIndex]));
            } else {
                stopMethods.push(() => spinner.stop(stops[spinnerIndex], reelset[spinnerIndex], (quickSpin ? this.quickStopGap : this.stopGap) * spinnerIndex, quickSpin));
            }
        }

        if (!anticipating) {
            // If not anticipating, all reels are stopped at the same time (the delay between reels is internal)
            return new Contract<void>((resolve) => {
                parallel(stopMethods).then(() => {
                    SoundManager.event(SoundEvent.reel_transition_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            // If anticipation is present, subsequent reels are delayed until the anticipation is finished (since the gap is unpredictable),
            // and then are stopped in parallel until the next anticipation reel and so on
            return new Contract<void>((resolve) => {

                let anticipationStarted = false;

                const getNextStop = (stop: number, parallels: Array<() => Contract<void>>): Contract<any> => {
                    if (stopMethods.length > stop) {
                        if (!anticipations[stop]) {
                            parallels.push(stopMethods[stop]);
                            return getNextStop(stop + 1, parallels);
                        } else {
                            return parallel(parallels).then(() => {
                                if (!anticipationStarted) {
                                    SoundManager.event(SoundEvent.reel_transition_anticipate);
                                    anticipationStarted = true;
                                }
                                stopMethods[stop]().then(() => getNextStop(stop + 1, []));
                            });
                        }
                    } else {
                        if (parallels.length) {
                            parallel(parallels).then(() => {
                                SoundManager.event(SoundEvent.reel_transition_end);
                                this.checkRaceComplete(() => resolve(null));
                            });
                        } else {
                            SoundManager.event(SoundEvent.reel_transition_end);
                            this.checkRaceComplete(() => resolve(null));
                        }
                    }
                };

                getNextStop(0, []);
            });
        }
    }

    public skip(stops: number[], reelset: string[][]): Contract<void> {
        this.skipping = true;
        let alreadyStopping = false;

        for (const spinner of this.spinners) {
            if (spinner.isStopping()) {
                alreadyStopping = true;
                break;
            }
        }

        if (!alreadyStopping || this.allowPartialSkips) {
            SoundManager.event(SoundEvent.reel_transition_skip);

            const skipMethods: Array<() => Contract<void>> = [];

            for (let i = 0; i < this.spinners.length; i++) {
                skipMethods.push(() => this.spinners[i].skip(stops[i], reelset[i]));
            }

            return new Contract<void>((resolve) => {
                parallel(skipMethods).then(() => {
                    SoundManager.event(SoundEvent.reel_transition_skip_end);
                    this.checkRaceComplete(() => resolve(null));
                });
            });
        } else {
            return new Contract<void>((resolve) => null);
        }
    }

    public jump(stops: number[], reelset: string[][]) {
        for (let i = 0; i < this.spinners.length; i++) {

            const spinner = this.spinners[i];

            spinner.jump(stops[i], reelset[i]);
        }
    }

    public reelLandedHandler(reelIndex: number) {
        this.onReelLand.dispatch(reelIndex);

        const lands = [];
        const landedReel = Components.matrix.getReels()[reelIndex - 1];
        const reelSymbols = landedReel.getSymbols();
        const validLandSymbols = this.anticipation.landSymbols[reelIndex - 1];
        const hasAnticipation = (this.anticipation && this.anticipation.anticipateSymbols.length > reelIndex);
        const symbolsToAnimate = hasAnticipation ? this.anticipation.anticipateSymbols[reelIndex] : [];
        for (const symbol of reelSymbols) {
            if (!validLandSymbols || validLandSymbols.indexOf(symbol.symbolId) > -1) {
                lands.push(() => symbol.land());
            }
        }

        parallel(lands).then(() => {
            this.spinningReels--;
            this.checkRaceComplete();

            if (!this.skipping) {
                if (symbolsToAnimate.length) {
                    let symbols: SymbolComponent[] = [];
                    const reels = Components.matrix.getReels().slice(0, reelIndex);
                    for (const reel of reels) {
                        if (reel.index <= reelIndex) {
                            symbols = symbols.concat(reel.getSymbols());
                        }
                    }
                    for (const symbol of symbols) {
                        if (symbolsToAnimate.indexOf(symbol.symbolId) > -1) {
                            if (this.anticipatingSymbols.indexOf(symbol) === -1) {
                                symbol.anticipate().execute();
                                this.anticipatingSymbols.push(symbol);
                            }
                        }
                    }
                }
            }
        });
    }

    public hold(reelIndexes?: number[]) {
        if (!reelIndexes) {
            reelIndexes = [];
        }
        this.heldReels = reelIndexes;
    }

    public update() {
        super.update();

        for (const spinner of this.spinners) {
            spinner.update();
        }
    }

    public getSpinners() {
        return this.spinners;
    }

    private stopAnticipatingSymbols() {
        for (const symbol of this.anticipatingSymbols) {
            symbol.static();
        }

        this.anticipatingSymbols = [];
    }

    // Resolves race condition between symbol land animation and reels stopping
    private checkRaceComplete(resolve?: Function) {
        if (resolve) {
            this.completeResolve = resolve;
        }

        if (this.spinningReels === 0) {
            if (this.completeResolve) {
                this.stopAnticipatingSymbols();
                this.completeResolve();
                this.completeResolve = null;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/matrix/reel/transition-behaviours/spin/spin-reel-transition.ts